// Toast Notification System
// Surfaces the engine's transient event narrative (regime shifts, sprint winners,
// risk events, model updates, F/1k spikes) as dismissible toast cards.
// Events auto-dismiss after configurable duration; CRITICAL events persist until dismissed.

import React, { useEffect, useRef, useState } from 'react';
import { useEngine } from '../context/MarketContext';
import type { ActivityEvent, ActivitySeverity } from '../types';

const SEVERITY_STYLES: Record<ActivitySeverity, { border: string; bg: string; icon: string; iconColor: string }> = {
  info:     { border: '#4da3ff', bg: '#0a0d10', icon: '●',  iconColor: '#4da3ff' },
  warning:  { border: '#ff8c3a', bg: '#0a0d10', icon: '⚠',  iconColor: '#ff8c3a' },
  critical: { border: '#ff5c5c', bg: '#1a0a0a', icon: '🚨', iconColor: '#ff5c5c' },
  positive: { border: '#36c98e', bg: '#0a1a12', icon: '✦',  iconColor: '#36c98e' },
};

const KIND_ICONS: Record<string, string> = {
  REGIME_SHIFT:  '🔄',
  SPRINT_WINNER: '🔥',
  F1K_SPIKE:     '⚡',
  MODEL_UPDATE:  '🧠',
  RISK_LIMIT:    '🚨',
  FILL:          '✅',
  ALERT:         '⚠',
};

const AUTO_DISMISS_MS: Record<ActivitySeverity, number | null> = {
  info:     5000,
  warning:  8000,
  critical: null,  // manual dismiss only
  positive: 6000,
};

interface ToastItem extends ActivityEvent {
  seenAt: number;
}

export default function ToastNotifications() {
  const { activityFeed } = useEngine();
  const [visible, setVisible] = useState<ToastItem[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const recentToastBySignatureRef = useRef<Map<string, number>>(new Map());

  // Pick up new events from the feed
  useEffect(() => {
    const now = Date.now();
    const newItems = activityFeed.filter(ev => {
      if (seenIdsRef.current.has(ev.id)) return false;
      const signature = `${ev.kind}|${ev.ticker ?? ''}|${ev.title}`;
      const lastShownAt = recentToastBySignatureRef.current.get(signature) ?? 0;
      // Suppress repeated identical toasts in a short window.
      if ((now - lastShownAt) < 15_000) return false;
      recentToastBySignatureRef.current.set(signature, now);
      return true;
    });
    if (newItems.length === 0) return;

    for (const ev of newItems) {
      seenIdsRef.current.add(ev.id);
    }

    setVisible(prev => {
      const added: ToastItem[] = newItems.map(ev => ({ ...ev, seenAt: now }));
      return [...added, ...prev].slice(0, 6); // cap at 6 visible toasts
    });

    // Schedule auto-dismiss for non-critical
    for (const ev of newItems) {
      const delay = AUTO_DISMISS_MS[ev.severity];
      if (delay != null) {
        const timer = setTimeout(() => dismiss(ev.id), delay);
        timersRef.current.set(ev.id, timer);
      }
    }
  }, [activityFeed]);

  const dismiss = (id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
    setVisible(prev => prev.filter(t => t.id !== id));
  };

  if (visible.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      bottom: 153,
      right: 24,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column-reverse',
      gap: 10,
      maxWidth: 340,
      pointerEvents: 'none',
    }}>
      {visible.map(toast => {
        const style = SEVERITY_STYLES[toast.severity];
        const icon = KIND_ICONS[toast.kind] || style.icon;

        return (
          <div
            key={toast.id}
            style={{
              backgroundColor: style.bg,
              border: `1px solid ${style.border}60`,
              borderLeft: `3px solid ${style.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              boxShadow: `0 4px 20px rgba(0,0,0,0.5), 0 0 0 1px ${style.border}20`,
              display: 'flex',
              gap: 10,
              alignItems: 'flex-start',
              pointerEvents: 'all',
              animation: 'slideIn 0.2s ease-out',
              minWidth: 260,
            }}
          >
            {/* Icon */}
            <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>{icon}</span>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, marginBottom: toast.detail ? 3 : 0,
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: '#d7dde4', flex: 1 }}>
                  {toast.title}
                </span>
                {toast.ticker && (
                  <span style={{
                    fontSize: 9, fontWeight: 700, color: style.border,
                    backgroundColor: `${style.border}15`,
                    border: `1px solid ${style.border}30`,
                    borderRadius: 3, padding: '1px 4px', flexShrink: 0,
                  }}>
                    {toast.ticker}
                  </span>
                )}
              </div>
              {toast.detail && (
                <div style={{ fontSize: 11, color: '#7e8a96', lineHeight: 1.4 }}>{toast.detail}</div>
              )}
              <div style={{ fontSize: 9, color: '#46525e', marginTop: 3 }}>
                {new Date(toast.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </div>
            </div>

            {/* Dismiss */}
            <button
              onClick={() => dismiss(toast.id)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#46525e', fontSize: 14, padding: 0, lineHeight: 1,
                flexShrink: 0, marginTop: 1,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        );
      })}

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
